import { createElement, useRef } from 'react';
import { useToggle, useMediaQuery, useDebouncedState, useResizeObserver } from '@react-hookz/web';
import cls from 'classnames';
import { useIntersection } from 'react-use';
import { pick } from 'lodash';
import omitDeep from 'omit-deep-lodash';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var _excluded$2 = ["hover", "mouse", "touch"];
/**
 * A HOC component that handles hover states
 *
 * @param {JSX.Element} Component The React component to wrap
 * @param {Object} [initialProps={hover: true}] Inital props for the wrapped component
 * @example withHover(Button, { hover: true })
 * @example withHover(Button, { mouse: true, touch: true })
 * @return {JSX.Element} Wrapped component
 */

var withHover = function withHover(Component, initialProps) {
  if (initialProps === void 0) {
    initialProps = {
      hover: true
    };
  }

  return function (_props) {
    var _initialProps$_props = _extends({}, initialProps, _props),
        hover = _initialProps$_props.hover,
        mouse = _initialProps$_props.mouse,
        touch = _initialProps$_props.touch,
        props = _objectWithoutPropertiesLoose(_initialProps$_props, _excluded$2);

    var _useToggle = useToggle(false),
        onHover = _useToggle[0],
        isHovering = _useToggle[1];

    var _useToggle2 = useToggle(false),
        onTouch = _useToggle2[0],
        isTouching = _useToggle2[1];

    var isMobile = useMediaQuery('(hover: none)', false);
    var binder = {
      hover: cls({
        _hover: onHover,
        _touch: onTouch
      }),
      onPointerEnter: function onPointerEnter() {
        return (hover || mouse && !isMobile) && isHovering(true);
      },
      onPointerLeave: function onPointerLeave() {
        return (hover || mouse && !isMobile) && isHovering(false);
      },
      onTouchStart: function onTouchStart() {
        return touch && isTouching(true);
      },
      onTouchEnd: function onTouchEnd() {
        return touch && isTouching(false);
      }
    };
    return /*#__PURE__*/createElement(Component, _extends({}, binder, props));
  };
};
/**
 * Use case: withHover(Button, { mouse: true })
 */


var _excluded$1 = ["rootMargin", "threshold", "paused", "idle"];
/**
 * A HOC component that handles CSS animation play state
 *
 * @param {JSX.Element} Component The React component to wrap
 * @example withPlayState(Marquee)
 * @returns {JSX.Element} Wrapped component
 */

var withPlaystate = function withPlaystate(Component) {
  return function (_ref) {
    var _ref$rootMargin = _ref.rootMargin,
        rootMargin = _ref$rootMargin === void 0 ? '100px' : _ref$rootMargin,
        _ref$threshold = _ref.threshold,
        threshold = _ref$threshold === void 0 ? 0 : _ref$threshold,
        paused = _ref.paused,
        idle = _ref.idle,
        props = _objectWithoutPropertiesLoose(_ref, _excluded$1);

    var ref = useRef(null);
    var intersection = useIntersection(ref, {
      root: null,
      rootMargin: rootMargin,
      threshold: threshold
    });
    var inView = !!(intersection && intersection.intersectionRatio > threshold);
    var classes = cls({
      '-paused': !inView || paused,
      '-idle': idle
    });
    return /*#__PURE__*/createElement(Component, _extends({
      playRef: ref,
      playClasses: classes,
      intersection: intersection,
      inView: inView
    }, props));
  };
};
/**
 * @callback With
 * @param {function} fn First HOC to apply
 * @param {...function} [hocs] Rest of HOCs to apply if any
 * @returns {JSX.Element} Wrapped component
 */

/**
 * @typedef {Object} HOC
 * @property {With} with Variadic function that take at least one HOC to apply
 */

/**
 * Allow to chain HOC on the provided component
 *
 * @param  {JSX.Element} Component The base component of the HOC
 * @example chain(Component).with(HOC1, HOC2, ...)
 * @returns {HOC} Object allowing to define the HOCs to chain
 */


var chain = function chain(Component) {
  return {
    with: function _with(fn) {
      for (var _len = arguments.length, hocs = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        hocs[_key - 1] = arguments[_key];
      }

      return hocs.reduce(function (WrappedComponent, hoc) {
        return hoc(WrappedComponent);
      }, fn(Component));
    }
  };
};
/**
 * Return CSS inset with fallbacks
 *
 * @param {String|array<string>} value
 * @returns
 */


var inset = function inset(value) {
  var styles = {
    number: {
      top: value,
      left: value,
      bottom: value,
      right: value
    },
    string: {
      top: value,
      left: value,
      bottom: value,
      right: value
    },
    array: {
      top: value[0],
      left: value[1],
      bottom: value[2],
      right: value[3]
    }[typeof value]
  };
  return {
    '@supports (inset: 0)': {
      inset: value
    },
    '@supports not (inset: 0)': styles
  };
};
/**
 * Return object
 *
 * @typedef {Object} PropertiesReturn
 * @property  {string} position
 * @property  {string} overflow
 * @property  {Object} ::before
 *
 */

/**
 * Generate borders with box-shadow (hack for subpixel stroke size)
 *
 * @param  {Object} obj CSS properties values
 * @param {?boolean} obj.top Should display border-top
 * @param {?boolean} obj.left Should display border-left
 * @param {?boolean} obj.right Should display border-right
 * @param {?boolean} obj.bottom Should display border-bottom
 * @param {?boolean} obj.x Should display border-left & border-right
 * @param {?boolean} obj.y Should display border-top & border-bottom
 * @param {string} obj.stroke='var(--stroke, 2px')' Stroke width
 * @param {?string} obj.offset='var(--offset, -30px')' Shadow offset (overflow)
 * @example shadowBorders({ top: true, bottom: true, stroke: 'var(--stroke)' })
 * @returns {PropertiesReturn} CSS properties
 */


var shadowBorders = function shadowBorders(_ref) {
  var top = _ref.top,
      left = _ref.left,
      right = _ref.right,
      bottom = _ref.bottom,
      x = _ref.x,
      y = _ref.y,
      _ref$stroke = _ref.stroke,
      stroke = _ref$stroke === void 0 ? 'var(--stroke, 2px)' : _ref$stroke,
      _ref$offset = _ref.offset,
      offset = _ref$offset === void 0 ? 'var(--offset, -30px)' : _ref$offset;

  var _ = function _(b) {
    return b ? 0 : offset;
  };

  return {
    position: 'relative',
    overflow: 'hidden',
    '::before': {
      content: '""',
      pointerEvents: 'none',
      position: 'absolute',
      top: _(y || top),
      left: _(x || left),
      right: _(x || right),
      bottom: _(y || bottom),
      boxShadow: "inset 0px 0px 0px " + stroke + " currentColor"
    }
  };
};
/**
 * Ensures string gets transformed as array
 *
 * @param  {string|Array.<string>} node The element to be checked or transformed
 * @returns {Array.<string>} Array of nodes
 */


var _asArray = function _asArray(node) {
  return Array.isArray(node) ? node : [node];
};
/**
 * Stringifies an object for usage in `<Source />`
 *
 * @param  {string} name Name of the main object key
 * @param  {object} obj The object to stringify
 * @returns {string} Stringified object
 */


var _printObject = function _printObject(name, obj) {
  return name + ": " + JSON.stringify(obj, null, 2).replace(/"(\w+)"\s*:/g, '$1:');
};

var _set = {
  /**
   * Quick attribute setter
   *
   * @param  {string} key
   * @param  {string|number} value
   */
  attr: function attr(key, value) {
    return document.documentElement.setAttribute(key, value);
  },

  /**
   * Quick attribute setter
   *
   * @param  {string} key
   * @param  {string|number} value
   */
  data: function data(key, value) {
    return document.documentElement.setAttribute("data-" + key, value);
  },

  /**
   * Quick attribute setter
   *
   * @param  {string} key
   * @param  {string|number} value
   */
  cssvar: function cssvar(key, value) {
    return document.documentElement.style.setProperty(key, value);
  },

  /**
   * Quick Classname setter
   *
   * @param  {string} name
   * @param  {bool} active
   */
  class: function _class(name, active) {
    if (active === void 0) {
      active = true;
    }

    var list = document.documentElement.classList;
    if (active) return list.add(name);
    return list.remove(name);
  }
};
/**
 * Generate CSS selectors from an array
 *
 * @param  {Array.<string>} [array=[]] Array of CSS Selectors
 * @param {Object} stringObject Used to generate the selector
 * @param {string} [stringObject.prev=''] String to add before each instances
 * @param {string} [stringObject.next=''] String to add after each instances
 * @param {string} [stringObject.sep=','] Character that joins all the instances
 *
 * @example
 * // returns 'html.a &, html.[b] &, html#c &'
 * cssSelectors(['.a', '[b]', '#c'], { prev: 'html', next: ' &' })
 * @returns {Array.<string>} Array of selectors
 */

var cssSelectors = function cssSelectors(array, _temp) {
  if (array === void 0) {
    array = [];
  }

  var _ref = _temp === void 0 ? {} : _temp,
      _ref$prev = _ref.prev,
      prev = _ref$prev === void 0 ? '' : _ref$prev,
      _ref$next = _ref.next,
      next = _ref$next === void 0 ? '' : _ref$next,
      _ref$sep = _ref.sep,
      sep = _ref$sep === void 0 ? ',' : _ref$sep;

  return array.map(function (node) {
    return "" + prev + node + next;
  }).join(sep);
};
/**
 * Extract the text content of a node
 *
 * @param  {any} node The node that will be scanned of text content
 *
 * @example getNodeText(props.children)
 * @returns {string}
 */


var getNodeText = function getNodeText(node) {
  if (['string', 'number'].includes(typeof node)) return node;
  if (node instanceof Array) return node.map(getNodeText).join('');
  if (typeof node === 'object' && node) return getNodeText(node.props.children);
};
/**
 * Generates crop values for a font
 *
 * @typedef {array} crops
 * @property {number} topCrop Top crop
 * @property {number} bottomCrop Bottom Crop
 * @property {number} atFontSize=36 At font size
 * @property {number} atLineHeight=1.2 At line-height
 *
 * @param {crops} crops Array containing the crop values
 * @param {number|string} lineHeight=1.2 Valid line-height value
 * @param {number} offsetTop=0 Top offset value
 * @param {number} offetsBot=0 Bottom offset value
 */


var lineHeightCrop = function lineHeightCrop(crops, lineHeight, offsetTop, offetsBot) {
  if (lineHeight === void 0) {
    lineHeight = 1.2;
  }

  if (offsetTop === void 0) {
    offsetTop = 0;
  }

  if (offetsBot === void 0) {
    offetsBot = 0;
  }

  var topCrop = crops[0],
      bottomCrop = crops[1],
      _crops$ = crops[2],
      atFontSize = _crops$ === void 0 ? 36 : _crops$,
      _crops$2 = crops[3],
      atLineHeight = _crops$2 === void 0 ? 1.2 : _crops$2;
  var unit = 'em';

  var calc = function calc(value) {
    return (value + (lineHeight - atLineHeight) * (atFontSize / 2)) / atFontSize;
  };

  var top = "-" + (calc(bottomCrop) + offetsBot) + unit;
  var bot = "-" + (calc(topCrop) + offsetTop) + unit;
  return {
    lineHeight: lineHeight,
    top: top,
    bot: bot
  };
};

var _excluded = ["as", "children"];
/**
 * @typedef ReturnedProps
 * @property {?string} target
 * @property {?string} rel
 * @property {string} aria-label
 */

/**
 * Set automatic A11y on links
 *
 * @param {Object} props Props value
 * @param {string} props.as The element the component should be redered as
 * @param {any} props.children The component children prop
 * @returns {ReturnedProps} New props
 */

var linkA11yProps = function linkA11yProps(_ref, inlined) {
  var as = _ref.as,
      children = _ref.children,
      props = _objectWithoutPropertiesLoose(_ref, _excluded);

  if (inlined === void 0) {
    inlined = false;
  }

  return _extends({
    as: as,
    target: as === 'a' ? '_blank' : undefined,
    rel: as === 'a' ? 'noreferrer noopener' : undefined,
    'aria-label': getNodeText(children),
    children: inlined ? children : null
  }, props);
};
/**
 * Reduces an object from an array of keys
 *
 * @param  {Object} list The source object
 * @param  {Array.<string>} [selected=[]] Array of keys selected from the source object
 * @returns {Object} Initial object containing only selected keys
 */


var listReducer = function listReducer(list, selected) {
  if (selected === void 0) {
    selected = [];
  }

  return Object.values(pick(list, selected)).reduce(function (acc, obj) {
    return _extends({}, acc, obj);
  }, {});
};
/**
 * Filter of filter-out an object with a regex expression ( and or ) array of keys
 *
 * @param  {Object} object Source object
 * @param  {Array.<string>} array=[] List of key to interact with
 * @param  {Object} props
 * @param  {RegExp} props.regex=false Regex rule to filter object
 * @param  {('pick'|'omit')} props.func="omit" Lodash function applied to the object
 */


var reduceObjectRegex = function reduceObjectRegex(object, array, _ref) {
  if (array === void 0) {
    array = [];
  }

  var _ref$regex = _ref.regex,
      regex = _ref$regex === void 0 ? null : _ref$regex,
      _ref$func = _ref.func,
      func = _ref$func === void 0 ? 'omit' : _ref$func;
  var mod = {
    pick: pick,
    omit: omitDeep
  }[func];
  return Object.entries(object).reduce(function (acc, _ref2) {
    var _extends3;

    var key = _ref2[0],
        value = _ref2[1];

    if (regex != null) {
      var _extends2;

      var keys = Object.keys(value).filter(function (a) {
        return regex.test(a);
      });
      return _extends({}, acc, (_extends2 = {}, _extends2[key] = mod(value, [].concat(keys, array)), _extends2));
    }

    return _extends({}, acc, (_extends3 = {}, _extends3[key] = mod(value, array), _extends3));
  }, {});
};

var useDebouncedRect = function useDebouncedRect(delay, maxWait) {
  if (delay === void 0) {
    delay = 300;
  }

  if (maxWait === void 0) {
    maxWait = 500;
  }

  var ref = useRef(null);

  var _useDebouncedState = useDebouncedState({}, delay, maxWait),
      rect = _useDebouncedState[0],
      setRect = _useDebouncedState[1];

  useResizeObserver(ref, function (e) {
    return setRect(e.contentRect);
  });
  return [ref, rect];
};

export { _asArray, _printObject, _set, chain, cssSelectors, getNodeText, inset, lineHeightCrop, linkA11yProps, listReducer, reduceObjectRegex, shadowBorders, useDebouncedRect as useDebounceRect, withHover, withPlaystate };